import React, { useState, useMemo } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell,
} from 'recharts';

interface ContributionData {
  coefficient: number;
  elasticity: number;
}

interface MediaChannel {
  name: string;
  column?: string;
}

interface WaterfallDataPoint {
  name: string;
  originalName: string;
  value: number;
  percentage: number;
  mediaSharePct: number | null;
  isMedia: boolean;
  fill: string;
}

interface ResponseDecompositionWaterfallProps {
  contributions: Record<string, ContributionData> | undefined;
  baselineValue?: number;
  title?: string;
  depVar?: string;
  mediaChannels?: MediaChannel[];
}

// Format currency values - handles small values better
const formatCurrency = (value: number): string => {
  const absValue = Math.abs(value);
  if (absValue >= 1000000) {
    return `$${(value / 1000000).toFixed(2)}M`;
  } else if (absValue >= 1000) {
    return `$${(value / 1000).toFixed(1)}K`;
  } else if (absValue >= 1) {
    return `$${value.toFixed(0)}`;
  } else if (absValue > 0) {
    return `$${value.toFixed(2)}`;
  } else {
    return '$0';
  }
};

export const ResponseDecompositionWaterfall: React.FC<ResponseDecompositionWaterfallProps> = ({
  contributions,
  baselineValue = 0,
  title = 'Response Decomposition by Predictor',
  depVar = 'revenue',
  mediaChannels = [],
}) => {
  const [showBaseline, setShowBaseline] = useState(false);

  // Build set of media channel names for quick lookup
  const mediaChannelNames = useMemo(() => {
    const names = new Set<string>();
    mediaChannels.forEach(mc => {
      // Add variations: name, name with spaces replaced, uppercase variants
      names.add(mc.name);
      names.add(mc.name.toLowerCase());
      names.add(mc.name.toUpperCase());
      names.add(mc.name.replace(/ /g, '_'));
      names.add(mc.name.replace(/_/g, ' '));
    });
    return names;
  }, [mediaChannels]);

  // Check if a feature name is a media channel
  const isMediaChannel = useMemo(() => {
    return (name: string): boolean => {
      const normalizedName = name.toLowerCase().replace(/_/g, ' ').trim();
      // Check direct match
      if (mediaChannelNames.has(name) || mediaChannelNames.has(normalizedName)) {
        return true;
      }
      // Check if any media channel name is contained in the feature name
      for (const mc of mediaChannels) {
        const mcName = mc.name.toLowerCase().replace(/_/g, ' ').trim();
        if (normalizedName.includes(mcName) || mcName.includes(normalizedName)) {
          return true;
        }
      }
      return false;
    };
  }, [mediaChannelNames, mediaChannels]);

  // Calculate data with percentages
  const { data, totalContribution, mediaTotal } = useMemo(() => {
    if (!contributions || Object.keys(contributions).length === 0) {
      return { data: [], totalContribution: 0, mediaTotal: 0 };
    }

    // Sort contributions by absolute elasticity value, descending
    const sortedContribs = Object.entries(contributions)
      .sort(([, a], [, b]) => Math.abs(b.elasticity) - Math.abs(a.elasticity))
      .slice(0, 10); // Top 10 for readability

    // Calculate totals
    const channelTotal = sortedContribs.reduce((sum, [, data]) => sum + Math.abs(data.elasticity), 0);
    const total = Math.abs(baselineValue) + channelTotal;

    // Calculate media-only total for media share %
    const mediaTotalCalc = sortedContribs
      .filter(([name]) => isMediaChannel(name))
      .reduce((sum, [, data]) => sum + Math.abs(data.elasticity), 0);

    const colors = [
      '#82ca9d',
      '#ffc658',
      '#ff7c7c',
      '#8dd1e1',
      '#d084d0',
      '#a4de6c',
      '#ffa07a',
      '#87ceeb',
      '#dda0dd',
      '#f0e68c',
    ];

    const chartData: WaterfallDataPoint[] = [];

    // Add baseline only if toggle is on
    if (showBaseline) {
      chartData.push({
        name: 'Baseline',
        originalName: 'baseline',
        value: baselineValue,
        percentage: total > 0 ? (Math.abs(baselineValue) / total) * 100 : 0,
        mediaSharePct: null,
        isMedia: false,
        fill: '#8884d8',
      });
    }

    sortedContribs.forEach(([name, contribData], index) => {
      const value = contribData.elasticity;
      const isMedia = isMediaChannel(name);
      chartData.push({
        name: name.replace(/_/g, ' ').toUpperCase(),
        originalName: name,
        value: value,
        percentage: total > 0 ? (Math.abs(value) / total) * 100 : 0,
        mediaSharePct: isMedia && mediaTotalCalc > 0
          ? (Math.abs(value) / mediaTotalCalc) * 100
          : null,
        isMedia,
        fill: value > 0 ? colors[index % colors.length] : '#ff6b6b',
      });
    });

    return {
      data: chartData,
      totalContribution: total,
      mediaTotal: mediaTotalCalc,
    };
  }, [contributions, baselineValue, showBaseline, isMediaChannel]);

  if (!contributions || Object.keys(contributions).length === 0) {
    return (
      <div className="w-full h-full bg-white p-6 rounded-lg border border-gray-200">
        <h3 className="text-lg font-bold text-gray-900 mb-4">{title}</h3>
        <div className="flex items-center justify-center h-96 bg-gray-50 rounded">
          <p className="text-gray-500">No contribution data available from model results</p>
        </div>
      </div>
    );
  }

  // Custom tooltip showing both $ value and percentage
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const item = payload[0].payload as WaterfallDataPoint;
      return (
        <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg min-w-[200px]">
          <p className="font-semibold text-gray-900 mb-2">{label}</p>
          <div className="space-y-1">
            <p className="text-gray-700">
              <span className="font-medium">Contribution:</span>{' '}
              <span className="text-indigo-600 font-semibold">{formatCurrency(item.value)}</span>
            </p>
            <p className="text-gray-600 text-sm">
              <span className="font-medium">% of Total {depVar}:</span> {item.percentage.toFixed(1)}%
            </p>
            {item.mediaSharePct !== null && (
              <p className="text-emerald-600 text-sm">
                <span className="font-medium">% of Media Attribution:</span>{' '}
                <span className="font-semibold">{item.mediaSharePct.toFixed(1)}%</span>
              </p>
            )}
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="w-full h-full bg-white p-6 rounded-lg border border-gray-200">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-bold text-gray-900">{title}</h3>
        <label className="flex items-center gap-2 cursor-pointer">
          <span className="text-sm text-gray-600">Show Baseline</span>
          <button
            onClick={() => setShowBaseline(!showBaseline)}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              showBaseline ? 'bg-indigo-600' : 'bg-gray-300'
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                showBaseline ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        </label>
      </div>

      {/* Summary stats */}
      <div className="mb-4 flex flex-wrap gap-4 text-sm">
        {!showBaseline && baselineValue !== 0 && (
          <div className="bg-gray-50 px-3 py-2 rounded">
            <span className="text-gray-500">Baseline:</span>{' '}
            <span className="font-semibold text-gray-700">{formatCurrency(baselineValue)}</span>
            <span className="text-gray-400 ml-1">
              ({totalContribution > 0 ? ((Math.abs(baselineValue) / totalContribution) * 100).toFixed(0) : 0}%)
            </span>
          </div>
        )}
        {mediaTotal > 0 && (
          <div className="bg-emerald-50 px-3 py-2 rounded">
            <span className="text-emerald-600">Total Media Attribution:</span>{' '}
            <span className="font-semibold text-emerald-700">{formatCurrency(mediaTotal)}</span>
          </div>
        )}
      </div>

      <ResponsiveContainer width="100%" height={400}>
        <BarChart
          data={data}
          margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis
            dataKey="name"
            angle={-45}
            textAnchor="end"
            height={100}
            interval={0}
            tick={{ fontSize: 12 }}
          />
          <YAxis
            label={{ value: `${depVar} Contribution`, angle: -90, position: 'insideLeft' }}
            tickFormatter={(value) => formatCurrency(value)}
          />
          <Tooltip content={<CustomTooltip />} />
          <Legend />
          <Bar dataKey="value" name="Contribution" radius={[8, 8, 0, 0]}>
            {data.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={entry.fill} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
      <div className="mt-4 text-sm text-gray-600">
        <p>
          <strong>Interpretation:</strong> Shows each predictor's contribution to total {depVar}.
          {mediaTotal > 0 && ` Media channels contributed ${formatCurrency(mediaTotal)} in total.`}
          {' '}Hover over bars to see dollar amounts and attribution percentages.
        </p>
      </div>
    </div>
  );
};
